import { useState, type KeyboardEvent } from 'react';
import { CheckIcon, ExclamationCircleIcon, PencilIcon } from '@heroicons/react/24/outline';
import { SolidStatusPill, Tooltip } from '@odo/shared-ui';
import { updateScanboxLedSupport, type DmPatient } from '../../../../../services/dmPatient';
import { Fact, Muted } from './DmFact';
import { Spinner, TOOLTIP_OVER_MODAL } from './DmControlPrimitives';
import { useDmControlMutation } from './useDmControlMutation';

const EDIT_BUTTON_CLASSES =
    'rounded text-gray-400 opacity-0 transition-opacity hover:text-gray-600 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 group-hover:opacity-100';

const SELECT_CLASSES =
    'block h-[1.5em] w-full cursor-pointer rounded border bg-white py-0 pl-1 text-[13px] font-normal leading-none focus:outline-none';

function LedPill({ led }: { led: boolean }) {
    return (
        <SolidStatusPill
            value={led ? 'led' : 'no-led'}
            label={led ? 'LED' : 'No LED'}
            toneClassName={led ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}
            dotClassName="bg-current opacity-70"
            className="shrink-0 leading-none"
            compact
        />
    );
}

function ledLabel(led: boolean | null): string {
    if (led === null) return 'Not set';
    return led ? 'Yes' : 'No';
}

interface LedSelectProps {
    current: boolean | null;
    failed: boolean;
    onPick: (led: boolean) => void;
    onCancel: () => void;
}

// The hidden option shows the current value, so picking it again still fires change and exits.
function LedSelect({ current, failed, onPick, onCancel }: LedSelectProps) {
    const [choice, setChoice] = useState('');

    // Stops the key here so the DM modal stays open while only the editor closes.
    const handleKeyDown = (e: KeyboardEvent<HTMLSelectElement>) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        onCancel();
    };

    return (
        <select
            value={failed ? '' : choice}
            aria-label="LED adapter"
            autoFocus
            onChange={(e) => {
                setChoice(e.target.value);
                onPick(e.target.value === 'true');
            }}
            onBlur={onCancel}
            onKeyDown={handleKeyDown}
            className={`${SELECT_CLASSES} ${
                failed
                    ? 'border-red-400 text-red-600'
                    : 'border-gray-300 text-gray-800 focus:border-[#007E87]'
            }`}
        >
            <option value="" hidden>
                {ledLabel(current)}
            </option>
            <option value="true">Yes</option>
            <option value="false">No</option>
        </select>
    );
}

function SaveState({ saving, error }: { saving: boolean; error: string | null }) {
    if (saving) return <Spinner className="h-3 w-3 text-[#007E87]" />;
    if (!error) return null;
    return (
        <Tooltip content={`Couldn't save: ${error}`} className={TOOLTIP_OVER_MODAL} focusable>
            <span className="inline-flex text-red-600">
                <ExclamationCircleIcon className="h-3.5 w-3.5" />
            </span>
        </Tooltip>
    );
}

interface ScanboxFactProps {
    patientId: string;
    patient: DmPatient;
}

export function ScanboxFact({ patientId, patient }: ScanboxFactProps) {
    const [editing, setEditing] = useState(false);
    const save = useDmControlMutation(
        patientId,
        (led: boolean) => updateScanboxLedSupport(patientId, led),
        () => setEditing(false)
    );
    const led = patient.hasScanboxLedSupport ?? null;

    const toggleEditing = (next: boolean) => {
        save.reset();
        setEditing(next);
    };
    const cancel = () => {
        if (!save.saving) toggleEditing(false);
    };
    const pick = (next: boolean) => {
        if (save.saving) return;
        if (next === led) toggleEditing(false);
        else save.run(next);
    };

    if (editing) {
        return (
            <Fact
                label="LED adapter"
                action={<SaveState saving={save.saving} error={save.error} />}
            >
                <LedSelect
                    current={led}
                    failed={save.error !== null}
                    onPick={pick}
                    onCancel={cancel}
                />
            </Fact>
        );
    }

    const viewActions = (
        <>
            {led !== null && <LedPill led={led} />}
            <Tooltip content="Edit LED adapter" className={TOOLTIP_OVER_MODAL}>
                <button
                    type="button"
                    onClick={() => toggleEditing(true)}
                    aria-label="Edit LED adapter"
                    className={EDIT_BUTTON_CLASSES}
                >
                    <PencilIcon className="h-3 w-3" />
                </button>
            </Tooltip>
        </>
    );

    return (
        <Fact label="ScanBox" fullText={patient.scanboxName} action={viewActions}>
            {patient.scanboxName ?? <Muted>None</Muted>}
            {save.saved && <CheckIcon className="ml-1 inline h-4 w-4 text-[#007E87]" />}
        </Fact>
    );
}
